"use client";
import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Space, Tooltip, Tag, Checkbox, Divider } from "antd";
import type { ColumnsType } from "antd/es/table";
import { type BaseRecord, type SelectOption } from "types";
import {
  ArrowsClockwise,
  Copy,
  DownloadSimple,
  Eye,
  Funnel,
  MagnifyingGlass,
  PencilSimple,
  TrashSimple,
} from "@phosphor-icons/react";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { TableViewCommonProps } from "types/table.types";
import {
  ButtonCommon,
  InputCommon,
  Pagination,
  SelectComponent,
  SwitchCommon,
} from "components/common";
import { Status } from "types/status.types";
import Popconfirm from "antd/lib/popconfirm";
import { isRenderResultObject } from "utils";
// Flexible Table Component with proper generics
const TableCommon = <T extends BaseRecord<unknown>>({
  columns,
  dataSource,
  searchable = true,
  actions = { edit: true, delete: true, copy: true, download: true },
  onEdit,
  onView,
  onDelete,
  onCopy,
  onDownload,
  displayView = false,
  defaultPageSize = 10,
  pageSizeOptions = ["5", "10", "20", "50", "100"],
  tableProps,
  count,
  onPaginationChange,
  handleToggleStatus,
  currentPage,
  searchColumnDefault,
  rowSelection,
  downLoadLoading,
  idFileDownload,
}: TableViewCommonProps<T>): React.ReactElement => {
  const params = useSearchParams();
  // State for search filters
  const [searchText, setSearchText] = useState<
    Record<string, string | undefined>
  >({
    ...searchColumnDefault?.reduce(
      (acc, curr) => {
        acc[curr.key] = curr.raw;
        return acc;
      },
      {} as Record<string, string | undefined>,
    ),
  });
  const [searchedColumn, setSearchedColumn] = useState(
    searchColumnDefault?.[0]?.key || "",
  );
  const [filteredData, setFilteredData] =
    useState<Record<string, string | undefined>>();
  const [data, setData] = useState<T[]>(dataSource);
  const [pagination, setPagination] = useState({
    current: currentPage || 1,
    pageSize: defaultPageSize,
  });

  const checkBoxRef = React.useRef<HTMLDivElement>(null);

  // Update filteredData when dataSource changes
  useEffect(() => {
    setData(dataSource);
  }, [dataSource]);
  // Apply search filters
  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilteredData(searchText);
  }, [searchText]);

  useEffect(() => {
    getPaginatedData();
  }, [pagination]);

  useEffect(() => {
    setPagination({
      pageSize: defaultPageSize || 10,
      current: currentPage || 1,
    });
  }, [currentPage, defaultPageSize]);
  // Handle pagination change
  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination({
      current: page,
      pageSize: pageSize || pagination.pageSize,
    });
  };

  // Handle page size change
  const handlePageSizeChange = (current: number, size: number) => {
    setPagination({
      current,
      pageSize: size,
    });
  };

  // Calculate paginated data
  const getPaginatedData = useCallback(() => {
    return onPaginationChange({
      page: pagination.current,
      size: pagination.pageSize,
      ...filteredData,
    }) as unknown as T[];
  }, [filteredData, pagination]);

  // Handle search input change
  const handleSearch = (columnKey: string, value: string | string[]): void => {
    let processedValue: string | string[];

    if (Array.isArray(value)) {
      processedValue = value;
    } else {
      processedValue = value?.trim() || "";
    }

    const valueArray =
      Array.isArray(processedValue) && processedValue?.length > 1
        ? processedValue
        : Array.isArray(processedValue)
          ? processedValue[0]
          : processedValue;

    setSearchText((prev) => ({
      ...prev,
      [columnKey]: Array.isArray(valueArray)
        ? valueArray.join(",")
        : valueArray,
    }));
    setSearchedColumn(columnKey);
  };

  const handleReset = (
    clearFilters: () => void,
    columnKey: string,
    confirm?: FilterDropdownProps["confirm"],
  ) => {
    const newSearchText = { ...searchText };
    localStorage.removeItem("customerListFilters");
    if (checkBoxRef.current) {
      const checkedWrappers = checkBoxRef.current.querySelectorAll(
        ".ant-checkbox-wrapper-checked",
      );
      checkedWrappers.forEach((wrapper) => {
        const checkedElement = wrapper.querySelector<HTMLInputElement>(
          ".ant-checkbox-checked",
        );
        if (checkedElement) {
          const input = wrapper.querySelector<HTMLInputElement>("input");
          if (input) {
            input.click();
          }
          checkedElement.classList.remove("ant-checkbox-checked");
          wrapper.classList.remove("ant-checkbox-wrapper-checked");
        }
      });
    }
    setSearchText({
      ...newSearchText,
      [columnKey]: undefined,
    });
    clearFilters();
    if (confirm) {
      confirm();
    }
  };

  // Check if a column has active filter
  const hasActiveFilter = (dataIndex: string): boolean => {
    const value = searchText[dataIndex];

    if (typeof value === "string") {
      return value.trim() !== "";
    }

    if (typeof value === "boolean") {
      return true;
    }

    return value !== undefined && value !== null;
  };

  // Add search inputs to columns
  const getColumns = (): ColumnsType<T> => {
    let newColumns = [...columns];
    if (searchable) {
      newColumns = newColumns.map((col) => {
        if ("dataIndex" in col) {
          const dataIndex = col.dataIndex as string;
          const isFiltered = hasActiveFilter(dataIndex);
          const isFilterSearch = col.filterSearch !== false;
          return {
            ...col,
            title: () => (
              <div className="flex items-center gap-2">
                {col.title && (
                  <span className="font-bold">{col.title?.toString()}</span>
                )}
              </div>
            ),
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
              close,
            }) =>
              isFilterSearch && (
                <div className="p-2 flex flex-col">
                  <div>
                    {col.filters ? (
                      <>
                        <div className="max-h-[300px] overflow-y-auto">
                          <Checkbox.Group
                            ref={checkBoxRef}
                            options={(col.filters || []).map((filter) => ({
                              label: filter.text,
                              value: filter.value,
                            }))}
                            onChange={(e) => {
                              handleSearch(dataIndex, e as string[]);
                              setSelectedKeys(e as string[]);
                            }}
                            className="flex flex-col gap-2"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <InputCommon
                          placeholder={`Tìm kiếm ${String(col.title?.toString()?.toLowerCase())}`}
                          value={searchText[dataIndex]}
                          defaultValue={searchText[dataIndex] ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedKeys(value ? [value] : []);
                          }}
                          onPressEnter={() => {
                            const value = (selectedKeys[0] as string)?.trim?.();
                            handleSearch(dataIndex, value ? [value] : []);
                          }}
                          className="w-full mb-2"
                          prefix={<MagnifyingGlass size={16} />}
                        />
                      </>
                    )}
                  </div>
                  <Divider className="my-2" />
                  {isFilterSearch && (
                    <>
                      <Space>
                        {!col.filters && (
                          <ButtonCommon
                            color="primary"
                            onClick={() => {
                              const value = (
                                selectedKeys[0] as string
                              )?.trim?.();
                              handleSearch(dataIndex, value ? [value] : []);
                            }}
                            size="xxs"
                            style={{ width: 90 }}
                            radius="rounded"
                          >
                            Tìm kiếm
                          </ButtonCommon>
                        )}
                        {
                          <ButtonCommon
                            color="outline"
                            onClick={() =>
                              clearFilters &&
                              handleReset(clearFilters, dataIndex, confirm)
                            }
                            size="xxs"
                            radius="rounded"
                            style={{ width: 90 }}
                          >
                            Xoá
                          </ButtonCommon>
                        }
                        <ButtonCommon
                          color="outline"
                          onClick={() => close && close()}
                          size="xxs"
                          radius="rounded"
                          style={{ width: 90 }}
                        >
                          Đóng
                        </ButtonCommon>
                      </Space>
                    </>
                  )}
                </div>
              ),
            // Fixed filterIcon to show active state
            filterIcon: isFilterSearch
              ? () => (
                  <div
                    className={`col-funnel transition-all duration-200 ${
                      isFiltered
                        ? "text-blue-600 bg-blue-50 rounded-full p-1"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                    style={{
                      color: isFiltered
                        ? "var(--primaryColor500)"
                        : "var(--textBlack54)",
                      backgroundColor: isFiltered
                        ? "var(--primaryColor50)"
                        : "transparent",
                      borderRadius: isFiltered ? "50%" : "0",
                      padding: isFiltered ? "4px" : "0",
                      transform: isFiltered ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <Funnel
                      size={16}
                      weight={isFiltered ? "fill" : "regular"}
                    />
                    {isFiltered && (
                      <div
                        className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                        style={{ backgroundColor: "var(--primaryColor500)" }}
                      />
                    )}
                  </div>
                )
              : () => null,
            // sortIcon(props) {
            //   return <IconSort props={props} />;
            // },
            render: (text: unknown, record: T, index: number) => {
              const getStatusKey = () =>
                (record["status.name"] || record?.status)
                  ?.toString()
                  ?.toUpperCase() as keyof typeof Status;

              const statusKey = getStatusKey();
              const statusValue = Status[statusKey];

              const isDraftLike = [Status.DRAFT, Status.DRAFT_PAYMENT].includes(
                statusValue,
              );
              const isSuccess = [Status.ACTIVE, Status.PAID].includes(
                statusValue,
              );
              const isDefference = statusValue === Status.PAYMENT_DEFERRENCE;
              const isWaitPayment =
                statusValue === Status.WAIT_PAYMENT ||
                statusValue === Status.INACTIVEVERSION;
              const isWaitConfirm = statusValue === Status.WAIT_CONFIRM;
              const isError = statusValue === Status.INACTIVE;
              const getStatusColor = () => {
                if (isDraftLike) return "var(--textBlack)";
                return "var(--textWhite)";
              };

              const getStatusBgColor = () => {
                if (isSuccess) return "var(--sematicSuccessColor500)";
                if (isDefference) return "var(--primaryColor900)";
                if (isWaitPayment) return "var(--primaryColor500)";
                if (isWaitConfirm) return "var(--sematicErrorColor600)";
                if (isError) return "var(--sematicErrorColor)";
                return "var(--bgBlack24)";
              };
              if (col.key === "isActive") {
                return (
                  <>
                    {handleToggleStatus ? (
                      <>
                        <SwitchCommon
                          label={
                            record.isActive ? "Kích hoạt" : "Chưa kích hoạt"
                          }
                          isActive={(record?.isActive as boolean) || false}
                          record={record}
                          onToggle={handleToggleStatus}
                        />
                      </>
                    ) : (
                      <>
                        <p
                          className={`text-white flex items-center justify-center rounded-3xl px-3 h-8  ${
                            record?.isActive
                              ? "bg-[var(--sematicSuccessColor500)]"
                              : "bg-[var(--textBlack56)]"
                          }`}
                        >
                          {record?.isActive
                            ? "Đang hoạt động"
                            : "Ngừng hoạt động"}
                        </p>
                      </>
                    )}
                  </>
                );
              }
              if (
                col.key === "status.code" ||
                col.key === "baseUser.isActive" ||
                col.key === "isActive"
              ) {
                return (
                  col.render ? col.render(text, record, index) : text
                ) as React.ReactNode;
              }

              const renderResult = col.render
                ? col.render(text, record, index)
                : text;

              let isObjectResult = false;
              let actualContent = renderResult;
              let actualProps: Record<string, unknown> | undefined;

              if (isRenderResultObject(renderResult)) {
                isObjectResult = true;
                actualContent = renderResult.children;
                actualProps = renderResult.props;
              }

              const innerContent = (
                <>
                  {col.key === "status" || col.key === "status.name" ? (
                    <Tag
                      color={
                        (record["status.name"] || record?.status) ===
                        Status.ACTIVE
                          ? "var(--sematicSuccessColor500)"
                          : "var(--textBlack24)"
                      }
                      className={
                        "leading-[32px] px-[12px] text-[14px] rounded-full"
                      }
                      style={{
                        color: getStatusColor(),
                        backgroundColor: getStatusBgColor(),
                      }}
                    >
                      {statusValue}
                    </Tag>
                  ) : col.key === "process" || col.key === "quantity" ? (
                    <span
                      style={{
                        color:
                          Number(record?.stock || 0) < 50
                            ? "var(--sematicErrorColor500)"
                            : "var(--sematicSuccessColor500)",
                      }}
                    >
                      {Number(record?.stock)}
                    </span>
                  ) : col.key === "category" || col.key === "category.name" ? (
                    <>{record["category.name"] || record?.category}</>
                  ) : col.key === "version" ? (
                    <SelectComponent
                      disabled={
                        (col as unknown & { disable: boolean })?.disable
                      }
                      data={(record?.dataSelect as SelectOption[])?.map(
                        (item) => ({
                          value: item.value,
                          label: item.label,
                        }),
                      )}
                      placeholder={"Tìm kiếm version"}
                      value={{
                        value: record?.version?.toString() ?? "",
                        label: "Ver " + (record?.version?.toString() ?? ""),
                      }}
                    />
                  ) : (
                    <>
                      {searchedColumn === dataIndex ? (
                        <Highlighter
                          highlightStyle={{
                            backgroundColor: "var(--highlightColor)",
                            padding: 0,
                          }}
                          searchWords={
                            typeof searchText[dataIndex] === "string"
                              ? (searchText[dataIndex] as string)
                                  ?.trim()
                                  .split(/\s+/) // Tách nhiều từ nếu có
                              : []
                          }
                          autoEscape
                          textToHighlight={text ? text.toString() : ""}
                        />
                      ) : (
                        actualContent
                      )}
                    </>
                  )}
                </>
              );

              if (isObjectResult) {
                return { children: innerContent, props: actualProps };
              }
              return innerContent;
            },
          };
        } else {
          // If col is a ColumnGroupType<T>, you can return it as is or handle it differently
          return col;
        }
      });
    }

    // Add actions column if needed
    if (actions && (actions.edit || actions.delete || actions.copy)) {
      newColumns.push({
        title: "ACTIONS",
        key: "actions",
        fixed: "right",
        width: 120,
        className: "actions-custom-column",
        render: (_: unknown, record: T, index: number) => (
          <Space
            size="small"
            className={
              params[0].get("newChange") && index === 0
                ? "bg-[var(--primaryColor50)]"
                : index % 2 !== 0
                  ? "w-[100%] bg-[var(--textBlack2)]"
                  : ""
            }
          >
            {actions.edit && onEdit && (
              <Tooltip title={displayView ? "Xem chi tiết" : "Chỉnh sửa"}>
                <Button
                  icon={
                    [
                      "WAIT_CONFIRM",
                      "PAYMENT_DEFERRENCE",
                      "PAID",
                      "WAIT_PAYMENT",
                    ].includes(
                      (record["status.name"] || record?.status) as string,
                    ) || displayView ? (
                      <Eye size={18} />
                    ) : (
                      <PencilSimple size={18} />
                    )
                  }
                  type="text"
                  onClick={() =>
                    [
                      "WAIT_CONFIRM",
                      "PAYMENT_DEFERRENCE",
                      "PAID",
                      "WAIT_PAYMENT",
                    ].includes(
                      (record["status.name"] || record?.status) as string,
                    ) || displayView
                      ? onView && onView(record)
                      : onEdit(record)
                  }
                />
              </Tooltip>
            )}
            {actions.download && onDownload && (
              <Tooltip title="Tải tệp tin">
                {idFileDownload === record.id && downLoadLoading ? (
                  <ArrowsClockwise size={18} className="animate-spin" />
                ) : (
                  <>
                    <Button
                      icon={<DownloadSimple size={18} />}
                      type="text"
                      onClick={() => onDownload(record)}
                    />
                  </>
                )}
              </Tooltip>
            )}
            {actions.copy && onCopy && (
              <Tooltip title="Tạo bản sao">
                <Button
                  icon={<Copy size={18} />}
                  type="text"
                  onClick={() => onCopy(record)}
                />
              </Tooltip>
            )}
            {actions.delete && onDelete && (
              <Tooltip title="Xoá">
                <Popconfirm
                  title="Xác nhận xoá"
                  description="Bạn có chắc muốn xoá lệnh này không?"
                  okText="Xoá"
                  cancelText="Huỷ"
                  placement="topRight"
                  disabled={[
                    "COMPLETED",
                    "TRANSPORTING",
                    "PICKED_UP",
                    "DELIVERED",
                    "EMPTY_RETURNED",
                    "CONTAINER_DROPPED_OFF",
                  ].includes(
                    (record["status.name"] || record?.status) as string,
                  )}
                  onConfirm={() => onDelete(record)}
                >
                  <Button
                    icon={<TrashSimple size={18} />}
                    disabled={[
                      "COMPLETED",
                      "TRANSPORTING",
                      "PICKED_UP",
                      "DELIVERED",
                      "EMPTY_RETURNED",
                      "CONTAINER_DROPPED_OFF",
                    ].includes(
                      (record["status.name"] || record?.status) as string,
                    )}
                    type="text"
                  />
                </Popconfirm>
              </Tooltip>
            )}
          </Space>
        ),
      });
    }

    return newColumns;
  };

  return (
    <div className="table-container">
      <AnimatePresence mode="wait">
        <motion.div
          key={pagination.current} // Change key when page changes
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="w-full"
        >
          <Table
            columns={getColumns()}
            dataSource={data}
            rowKey={(record) => {
              const recordId = record.id || record.key;
              if (
                typeof recordId === "string" ||
                typeof recordId === "number"
              ) {
                return String(recordId);
              }
              return JSON.stringify(record);
            }}
            pagination={false}
            className="w-full px-[24px]"
            rowClassName={(_, index) =>
              params[0].get("newChange") && index === 0
                ? "new-change-row bg-[var(--primaryColor50)]"
                : index % 2 !== 0
                  ? "bg-[var(--textBlack2)]"
                  : ""
            }
            rowSelection={rowSelection}
            {...tableProps}
          />
        </motion.div>
      </AnimatePresence>

      {/* Use reusable Pagination component */}
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={count || 0}
        pageSizeOptions={pageSizeOptions}
        onChange={handlePaginationChange}
        onPageSizeChange={handlePageSizeChange}
        className="border-t border-gray-200"
      />
    </div>
  );
};

export default TableCommon;
